import ClientBookForm from '@/features/public/booking/components/ClientBookForm.tsx';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  ClientFormWithCustomFields,
  ClientInputFormWithCustomFields,
  createClientSchema,
  generateFormKey,
} from '@/features/public/booking/schemas/clientSchema.ts';
import { debugZodResolver } from '@/lib/debugZodResolver';
import ParticipantForm from '@/features/public/booking/components/ParticipantForm.tsx';
import { participantCardId } from '@/features/public/booking/util/participantCardId.ts';
import focusNextFieldOnEnter from '@/components/form/focusNextFieldOnEnter.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { PlusIcon, TriangleAlert } from 'lucide-react';
import {
  bookingDraftKey,
  draftOf,
  useBookingStore,
} from '@/features/public/booking/context/bookingStore.tsx';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { bookingRoute } from '@/features/public/booking/routes/bookingRoutes.ts';
import BookingActionFooter from '@/features/public/booking/components/BookingActionFooter.tsx';
import { useInitParticipantValues } from '@/features/public/booking/hooks/useInitParticipantValues.ts';
import { bookingTourRoute } from '@/features/public/booking/routes/bookingTourRoute.ts';
import { BookingScope } from '@/features/shared/booking/types/BookingScope.ts';
import { useEffect, useRef, useState } from 'react';
import { FunnelEventStage } from '@/features/public/booking/tracking/FunnelEventStage.ts';
import { trackFunnelStage } from '@/features/public/booking/tracking/funnelTracker.ts';
import { BookingQuoteProvider } from '@/features/public/booking/context/BookingQuoteContext.tsx';
import {
  bookingQuoteEstimateQueryOptions,
  canEstimateBookingQuote,
} from '@/features/public/booking/hooks/useGetBookingQuote.ts';
import { getDefaultBookingFormData } from '@/features/public/booking/util/bookingFormDefaults.ts';
import { hasVisiblePricingErrors } from '@/features/public/booking/util/pricingErrorPlacement.ts';
import { scrollToFirstFormError } from '@/utils/scrollToFirstFormError.ts';

const BookingPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgSlug, tourSlug } = bookingTourRoute.useParams();

  const tourKey = bookingDraftKey(orgSlug, tourSlug);
  // Zdarzenie, nie `useEffect` na `isDirty`: pierwsza zmiana pola ma odpalić
  // to raz, a nie przy każdym przejściu stanu formularza.
  const formStartReported = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [blockedSubmits, setBlockedSubmits] = useState(0);
  const [blockedWithoutMessage, setBlockedWithoutMessage] = useState(false);
  const [submittedFormKey, setSubmittedFormKey] = useState<string>();
  const setClientFormData = useBookingStore((state) => state.setClientFormData);
  const savedFormData = useBookingStore(
    (state) => draftOf(state, tourKey).clientFormData,
  );

  const { participantForm, tourBasicInfo, bookingInsurances } =
    bookingRoute.useLoaderData();

  const scope: BookingScope = { type: 'public', orgSlug, tourSlug };

  const { getParticipantInitValues } =
    useInitParticipantValues(participantForm);

  const clientForm = useForm<ClientInputFormWithCustomFields>({
    resolver: debugZodResolver(
      createClientSchema(
        participantForm ?? [],
        tourBasicInfo,
        bookingInsurances,
      ),
    ),
    defaultValues: getDefaultBookingFormData(
      savedFormData,
      getParticipantInitValues,
    ),
    // Zablokowana wysyłka tylko przewija do komunikatu (`scrollToFirstFormError`),
    // bez kursora — na telefonie fokus otwiera klawiaturę. Własne ogniskowanie
    // RHF do tego pomija pola z `Controller` bez refa (data urodzenia) i
    // skakało do następnego pola w dokumencie — czyli nie tam, gdzie ekran.
    shouldFocusError: false,
  });
  const {
    handleSubmit,
    formState: { errors, isSubmitting, submitCount },
  } = clientForm;

  const { fields, append, remove, update } = useFieldArray<
    ClientInputFormWithCustomFields,
    'participants'
  >({
    control: clientForm.control,
    name: 'participants',
    shouldUnregister: true,
  });

  // Komunikaty — i te z Zoda, i te z wyceny — trafiają do DOM-u dopiero po
  // przerysowaniu, więc przewijanie musi poczekać na efekt.
  useEffect(() => {
    if (blockedSubmits === 0) return;
    // Zatrzymana wysyłka bez ani jednego komunikatu to ślepe kliknięcie —
    // wtedy (i tylko wtedy) mówimy o tym pod przyciskiem.
    setBlockedWithoutMessage(!scrollToFirstFormError(formRef.current));
  }, [blockedSubmits]);

  useEffect(() => {
    if (blockedWithoutMessage) scrollToFirstFormError(formRef.current);
  }, [blockedWithoutMessage]);

  const blockSubmit = () => setBlockedSubmits((count) => count + 1);

  // Nowa karta ląduje pod stopką, poza ekranem — przewijamy do niej i od razu
  // stawiamy kursor w imieniu, bo to jedyne, co można tam zrobić. Pole
  // istnieje dopiero po przerysowaniu, stąd `requestAnimationFrame`.
  const addParticipant = () => {
    const index = fields.length;
    append(getParticipantInitValues());
    requestAnimationFrame(() => {
      document
        .getElementById(participantCardId(index))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      formRef.current
        ?.querySelector<HTMLInputElement>(
          `input[name="participants.${index}.firstName"]`,
        )
        ?.focus({ preventScroll: true });
    });
  };

  const onSubmit = async (data: ClientFormWithCustomFields) => {
    // Wycena w tle jest opóźniona o pół sekundy, więc kliknięcie tuż po
    // ostatnim wyborze zastałoby ją nieaktualną. Klucz zapytania niesie stan
    // formularza, więc świeżą odpowiedź zwykle oddaje cache bez kolejnej rundy
    // — a podany providerowi sprawia, że blokujemy tą samą wyceną, którą
    // klient zaraz zobaczy przy polach.
    const formKey = generateFormKey(data);
    setSubmittedFormKey(formKey);

    let quote;
    // Ten sam warunek co w estymacie: bez kompletu dat urodzenia backend
    // odbiłby żądanie, a nie ma czego liczyć.
    if (canEstimateBookingQuote(data)) {
      try {
        quote = await queryClient.fetchQuery(
          bookingQuoteEstimateQueryOptions(
            orgSlug,
            tourSlug,
            data,
            participantForm ?? [],
            formKey,
          ),
        );
      } catch {
        // Niedostępna wycena nie jest powodem, żeby zatrzymać klienta na kroku 1
        // — podsumowanie liczy się jeszcze raz i tam błąd i tak wyjdzie.
        quote = undefined;
      }
    }

    const blocked = (quote?.pricesPerParticipants ?? []).some((participant) =>
      hasVisiblePricingErrors(participant.errors),
    );
    if (blocked) {
      blockSubmit();
      return;
    }

    setClientFormData(tourKey, data);
    // Spinner na przycisku ma trwać, aż podsumowanie stanie na ekranie — bez
    // `await` gasłby w chwili wywołania, a klient czekałby na przejście bez
    // żadnego sygnału.
    await navigate({
      to: '/booking/$orgSlug/$tourSlug/summary',
      params: { orgSlug, tourSlug },
    });
  };

  const participantsWithErrors = fields
    .map((_, index) => index)
    .filter((index) => !!errors.participants?.[index]);

  return (
    <FormProvider {...clientForm}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit, blockSubmit)}
        onKeyDown={focusNextFieldOnEnter}
        onChange={() => {
          if (formStartReported.current) return;
          formStartReported.current = true;
          trackFunnelStage(orgSlug, tourSlug, FunnelEventStage.FORM_STARTED);
        }}
        className="space-y-2"
      >
        <BookingQuoteProvider
          orgSlug={orgSlug}
          tourSlug={tourSlug}
          participantForm={participantForm ?? []}
          submittedFormKey={submittedFormKey}
        >
          <ClientBookForm
            updateParticipant={update}
            participantForm={participantForm}
          />

          {fields.map((field, index) => (
            <ParticipantForm
              key={field.id}
              index={index}
              remove={remove}
              participantsCount={fields.length}
              participantForm={participantForm}
              scope={scope}
              bookingInsurances={bookingInsurances}
              transport={tourBasicInfo.transport}
              tourStartDate={tourBasicInfo.startDate}
            />
          ))}

          <InputError
            error={
              errors.participants?.root?.message ?? errors.participants?.message
            }
          />

          {blockedWithoutMessage && (
            <InputError error="Nie możemy przejść dalej z obecnym wyborem. Odśwież stronę i sprawdź wybrane opcje oraz transport." />
          )}

          {submitCount > 0 && participantsWithErrors.length > 1 && (
            <p className="flex items-center gap-1.5 text-sm text-error-200">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              Popraw błędy w formularzu:{' '}
              {participantsWithErrors
                .map((index) => `Uczestnik ${index + 1}`)
                .join(', ')}
              .
            </p>
          )}

          <div className="flex flex-col justify-between gap-2 md:flex-row">
            <ActionButton tone="outline" size="sm" onClick={addParticipant}>
              <PlusIcon />
              Dodaj uczestnika
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={isSubmitting}
            >
              Przejdź do podsumowania
            </ActionButton>
          </div>

          <div className="mb-50" />

          <BookingActionFooter onSubmit={handleSubmit(onSubmit, blockSubmit)} />
        </BookingQuoteProvider>
      </form>
    </FormProvider>
  );
};

export default BookingPage;
